import React from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

// Hide number input spinners globally for this component
const numberInputStyles = {
  '& input[type=number]': {
    '-moz-appearance': 'textfield',
  },
  '& input[type=number]::-webkit-outer-spin-button': {
    '-webkit-appearance': 'none',
    margin: 0,
  },
  '& input[type=number]::-webkit-inner-spin-button': {
    '-webkit-appearance': 'none',
    margin: 0,
  },
};

// Service item type - exported for reuse
export type ServiceItem = {
  id: string;
  serviceType: string;
  partName: string;
  partQuantity: number;
  unitPrice: number;
  totalPartCost: number;
  serviceDescription: string;
};

// Service types constant - exported for reuse
export const SERVICE_TYPES = [
  'Oil Change',
  'Filter Replacement',
  'Air Filter',
  'Brake Service',
  'Tyre Service',
  'Battery Service',
  'General Service',
  'Repair',
  'Wheel Alignment',
  'Cooling System',
  'Electrical',
  'Suspension',
  'Transmission',
  'Other'
];

interface ServiceItemsEditorProps {
  items: ServiceItem[];
  onAdd: () => void;
  onRemove: (id: string) => void;
  onUpdate: (id: string, field: keyof ServiceItem, value: any) => void;
  showTotal?: boolean;
  readOnly?: boolean;
}

const ServiceItemsEditor: React.FC<ServiceItemsEditorProps> = ({
  items,
  onAdd,
  onRemove,
  onUpdate,
  showTotal = true,
  readOnly = false,
}) => {
  // Calculate total parts cost
  const totalPartsCost = items.reduce((sum, item) => sum + (item.totalPartCost || 0), 0);

  console.log('ServiceItemsEditor - items:', items);
  console.log('ServiceItemsEditor - onUpdate:', onUpdate);

  // Debug: Add item handler
  const handleAddClick = () => {
    console.log('Add Item clicked');
    onAdd();
  };

  return (
    <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Service Items / Parts</Typography>
        {!readOnly && (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            variant="contained"
            sx={{ backgroundColor: 'var(--primary-color)' }}
          >
            Add Item
          </Button>
        )}
      </Box>

      {items.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          No service items. Click "Add Item" to add parts/services.
        </Typography>
      ) : (
        items.map((item, index) => (
          <Box
            key={item.id}
            sx={{
              mb: 2,
              p: 2,
              bgcolor: 'white',
              borderRadius: 1,
              border: '1px solid #e0e0e0'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2">Item {index + 1}</Typography>
              {!readOnly && (
                <IconButton
                  size="small"
                  onClick={() => onRemove(item.id)}
                  color="error"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            <Grid container spacing={1}>
              <Grid size={{ xs: 12, sm: 6 }}>
                {readOnly ? (
                  <Box>
                    <Typography variant="caption" color="textSecondary">Service Type</Typography>
                    <Typography variant="body2">{item.serviceType || '-'}</Typography>
                  </Box>
                ) : (
                  <FormControl fullWidth size="small">
                    <InputLabel>Service Type</InputLabel>
                    <Select
                      value={item.serviceType}
                      onChange={(e) => {
                        console.log('Select onChange - id:', item.id, 'field: serviceType, value:', e.target.value);
                        onUpdate(item.id, 'serviceType', e.target.value);
                      }}
                      label="Service Type"
                    >
                      {SERVICE_TYPES.map((type) => (
                        <MenuItem key={type} value={type}>{type}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                {readOnly ? (
                  <Box>
                    <Typography variant="caption" color="textSecondary">Part Name</Typography>
                    <Typography variant="body2">{item.partName || '-'}</Typography>
                  </Box>
                ) : (
                  <TextField
                    size="small"
                    fullWidth
                    label="Part Name"
                    value={item.partName}
                    onChange={(e) => onUpdate(item.id, 'partName', e.target.value)}
                  />
                )}
              </Grid>
              <Grid size={{ xs: 4, sm: 4 }}>
                {readOnly ? (
                  <Box>
                    <Typography variant="caption" color="textSecondary">Qty</Typography>
                    <Typography variant="body2">{item.partQuantity}</Typography>
                  </Box>
                ) : (
                  <TextField
                    size="small"
                    fullWidth
                    label="Qty"
                    type="number"
                    inputProps={{ min: 1 }}
                    value={item.partQuantity}
                    onChange={(e) => onUpdate(item.id, 'partQuantity', Math.max(1, parseInt(e.target.value) || 1))}
                  />
                )}
              </Grid>
              <Grid size={{ xs: 4, sm: 4 }}>
                {readOnly ? (
                  <Box>
                    <Typography variant="caption" color="textSecondary">Unit Price</Typography>
                    <Typography variant="body2">₹{item.unitPrice?.toFixed(2) || '0.00'}</Typography>
                  </Box>
                ) : (
                  <TextField
                    size="small"
                    fullWidth
                    label="Unit Price (₹)"
                    type="number"
                    inputProps={{ min: 0 }}
                    value={item.unitPrice}
                    onChange={(e) => onUpdate(item.id, 'unitPrice', Math.max(0, parseFloat(e.target.value) || 0))}
                  />
                )}
              </Grid>
              <Grid size={{ xs: 4, sm: 4 }}>
                <TextField
                  size="small"
                  fullWidth
                  label="Total (₹)"
                  type="number"
                  value={item.totalPartCost}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                {readOnly ? (
                  <Box>
                    <Typography variant="caption" color="textSecondary">Description</Typography>
                    <Typography variant="body2">{item.serviceDescription || '-'}</Typography>
                  </Box>
                ) : (
                  <TextField
                    size="small"
                    fullWidth
                    label="Description"
                    value={item.serviceDescription}
                    onChange={(e) => onUpdate(item.id, 'serviceDescription', e.target.value)}
                  />
                )}
              </Grid>
            </Grid>
          </Box>
        ))
      )}

      {showTotal && items.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Typography variant="subtitle2">
            Total Parts Cost: ₹{totalPartsCost.toFixed(2)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ServiceItemsEditor;
